import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { PageErrorFallback } from "@/components/error/ErrorFallback";
import { registerSendMessageV2Defaults } from "@/modules/conversations/hooks/useSendMessageV2";
import { PERSISTED_CACHE_KEY, shouldPersistQuery, shouldPersistMutation } from "@/lib/query-persistence";

// Import contexts
import { AuthProvider } from "@/contexts/AuthContext";
import { PlatformAdminProvider } from "@/contexts/PlatformAdminContext";
import { TenantSelectionProvider } from "@/contexts/TenantSelectionContext";
import { AISuggestionsProvider } from "@/contexts/AISuggestionsContext";
import { AICopilotProvider } from "@/contexts/AICopilotContext";
import { GlobalAICopilot } from "@/components/ai/GlobalAICopilot";
import { UserImpersonationProvider } from "@/contexts/UserImpersonationContext";
import { ImpersonationBar } from "@/components/platform-admin/ImpersonationBar";

// Import lazy-loaded pages
import {
  PageLoader,
  lazyRetry,
  LazyPlatformAdmin,
  LazyVoiceLab,
  LazyPlatformAdminUsers,
  LazyPlatformAdminTenants,
  LazyPlatformAdminFormLinks,
  LazyPlatformAdminSpaceWizard,
  LazyPlatformAdminBilling,
  LazyPlatformAdminLogging,
  LazyPlatformAdminAPIHealth,
  LazyPlatformAdminRehearsalEnvironment,
  LazyPlatformAdminTesting,
  LazyPlatformAdminAIManagement,
  LazyPlatformAdminTestModels,
  LazyPlatformAdminAITemplates,
  LazyPlatformAdminTemplates,
  LazyPlatformAdminConversationTesting,
  LazyPlatformAdminConversationSimulator,
  LazyPlatformAdminTenantUsers,
  LazyPlatformAdminTenantSettings,
  LazyPlatformAdminTenantRuntimeConfig,
  LazyPlatformAdminTenantAIAgents,
  LazyPlatformAdminTenantWorkflowBuilder,
  LazyPlatformAdminTenantProducts,
  LazyPlatformAdminTenantWhatsApp,
  LazyPlatformAdminTenantAnalytics,
  LazyPlatformAdminTenantLogging,
  LazyPlatformAdminTenantConversations,
  LazyWhatsAppAdminPlatform,
  LazySystemHealth,
  LazyTenantWhatsAppFlows,
  LazyTenantWorkflowBuilder,
  LazyTenantCustomExtensions,
  LazyTenantConversationSimulator,
  LazyConversationsAnalytics,
  LazyTenantEmailTemplates,
  LazyTenantAdminCalendarBooking,
  LazyTenantAdminCalendarConfig,
  LazyAppStudioBuilder,
  LazyTenantAppStudio,
  LazyTenantAppStudioApp,
  LazyTenantAppStudioPageViewer,
  LazyTenantAIAgents,
  LazyTenantAIWorkflows,
  LazyTenantAISettings,
  LazyTenantContextStores,
  LazyEscalationSettings,
  LazyPaymentSettings,
  LazyTenantAdminCredentials,
  LazySettings,
} from "./lazy-routes";

// Import static pages (frequently accessed, kept in main bundle)
// Eager on purpose: going back from the new conversations screen must be a re-render, never a download
// (asserted by ConversationsSwitch.test.tsx).
import ConversationsSwitch from "./pages/ConversationsSwitch";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import SecureAdminPortal from "./pages/SecureAdminPortal";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentFailed from "./pages/PaymentFailed";
import PaymentPending from "./pages/PaymentPending";
import NotFound from "./pages/NotFound";
import ActivateAccount from "./pages/ActivateAccount";
import ConfirmSignup from "./pages/ConfirmSignup";
import VerifyEmail from "./pages/VerifyEmail";
import ResetPassword from "./pages/ResetPassword";
import ComingSoon from "./pages/ComingSoon";
import SentryTest from "./pages/SentryTest";

// Import tenant feature selector
import { TenantFeatureSelector } from "@/components/platform-admin/TenantFeatureSelector";
import { TenantSelectionInterceptor } from "@/components/platform-admin/TenantSelectionInterceptor";
import { PlatformAdminInterceptor } from "@/components/PlatformAdminInterceptor";

// Import route guards
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ModuleGuard } from "@/components/ModuleGuard";
import { PlatformAdminRoute } from "@/components/PlatformAdminRoute";
import { AuthInitializer } from "@/components/auth/AuthInitializer";

// Tenant screens, kept out of the entry bundle: every route below is wrapped in the Suspense around <Routes>.
const Dashboard = lazyRetry(() => import("./pages/Dashboard"));
const TenantConversationDetails = lazyRetry(() => import("./pages/TenantConversationDetails"));
const TenantConversationMessages = lazyRetry(() => import("./pages/TenantConversationMessages"));
const TenantAdmin = lazyRetry(() => import("./pages/TenantAdmin"));
const TenantUserManagement = lazyRetry(() => import("./pages/TenantUserManagement"));
const TenantBilling = lazyRetry(() => import("./pages/TenantBilling"));
const TenantTesting = lazyRetry(() => import("./pages/TenantTesting"));
const TenantProductsServices = lazyRetry(() => import("./pages/TenantProductsServices"));
const TenantTireManagement = lazyRetry(() => import("./pages/TenantTireManagement"));
const TenantAdminProducts = lazyRetry(() => import("./pages/TenantAdminProducts"));
const TenantAdminCustomers = lazyRetry(() => import("./pages/TenantAdminCustomers"));
const TenantAdminOrders = lazyRetry(() => import("./pages/TenantAdminOrders"));
const TenantAdminDocumentReview = lazyRetry(() => import("./pages/TenantAdminDocumentReview"));
const TenantAdminBusinessHours = lazyRetry(() => import("./pages/TenantAdminBusinessHours"));
const TenantAdminRuleSets = lazyRetry(() => import("./pages/TenantAdminRuleSets"));
const TenantAdminDocumentTypes = lazyRetry(() => import("./pages/TenantAdminDocumentTypes"));
const TenantAdminAPIMonitoring = lazyRetry(() => import("./pages/TenantAdminAPIMonitoring"));
const TenantAdminRandomERPTesting = lazyRetry(() => import("./pages/TenantAdminRandomERPTesting"));
const TenantAdminPartnerDirectory = lazyRetry(() => import("./pages/TenantAdminPartnerDirectory"));
const TenantTemplatesLayout = lazyRetry(() => import("./pages/tenant-admin/TenantTemplatesLayout"));
const EmailProviderSettings = lazyRetry(() => import("./pages/tenant-admin/EmailProviderSettings"));
const TenantWhatsAppTemplates = lazyRetry(() => import("./pages/tenant-admin/whatsapp-templates/TenantWhatsAppTemplates"));
const WhatsAppSettingsTab = lazyRetry(() => import("./pages/tenant-admin/whatsapp-templates/WhatsAppSettingsTab"));
const WhatsAppTemplatesTab = lazyRetry(() => import("./pages/tenant-admin/whatsapp-templates/WhatsAppTemplatesTab"));
const WhatsAppAnalyticsTab = lazyRetry(() => import("./pages/tenant-admin/whatsapp-templates/WhatsAppAnalyticsTab"));
const QuickRepliesManagement = lazyRetry(() => import("./pages/QuickRepliesManagement"));
const WhatsAppAdminRegular = lazyRetry(() => import("./pages/WhatsAppAdminRegular"));
const Onboarding = lazyRetry(() => import("./pages/Onboarding"));
const FormLink = lazyRetry(() => import("./pages/FormLink"));
const PlatformAdminWebhookSecurity = lazyRetry(() => import("./components/platform-admin/webhook/PlatformAdminWebhookSecurity").then((m) => ({ default: m.PlatformAdminWebhookSecurity })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // gcTime > persistence buster window — required so persisted queries hydrate cleanly
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
    },
  },
});

// Register V2 send-message mutation defaults (TanStack canonical offline pattern).
// MUST run before any consumer of useSendMessageV2 (which is per-component).
// The defaults provide: mutationFn (executeSend), onMutate (optimistic insert),
// onSuccess (cache reconciliation), onError (classify + cache failure marker),
// retry (classifier-aware), scope (per-conversation serial ordering).
registerSendMessageV2Defaults(queryClient);

const localStoragePersister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  key: PERSISTED_CACHE_KEY,
  // throttleTime defaults to 1000ms — fine for our usage
});

function App() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: localStoragePersister,
        maxAge: 1000 * 60 * 60 * 24, // 24 hours
        buster: 'v2-send-pipeline-v1', // bump to invalidate persisted cache on schema changes
        dehydrateOptions: {
          // What survives a reload, and why, is argued in src/lib/query-persistence.ts. Both
          // predicates are declared: leaving shouldDehydrateQuery out is what made every
          // successful query in the whole application end up on the operator's disk.
          shouldDehydrateQuery: shouldPersistQuery,
          shouldDehydrateMutation: shouldPersistMutation,
        },
      }}
      onSuccess={() => {
        // Resume any paused mutations after the cache hydrates from localStorage
        queryClient.resumePausedMutations().then(() => {
          queryClient.invalidateQueries();
        });
      }}
    >
      <Sentry.ErrorBoundary
        fallback={({ error, resetError }) => (
          <PageErrorFallback error={error} resetErrorBoundary={resetError} />
        )}
      >
        <TooltipProvider>
          <BrowserRouter>
            <AuthProvider>
              <AuthInitializer />
              <PlatformAdminProvider>
                <TenantSelectionProvider>
                  <UserImpersonationProvider>
                    <AISuggestionsProvider>
                      <AICopilotProvider>
                      <ImpersonationBar />
                      <Suspense fallback={<PageLoader />}>
                      <Routes>
                    {/* ========== PUBLIC ROUTES ========== */}
                     <Route path="/" element={<Index />} />
                     <Route path="/auth" element={<Auth />} />
                     <Route path="/secure-admin-portal" element={<SecureAdminPortal />} />
                     <Route path="/activate-account" element={<ActivateAccount />} />
                     <Route path="/confirm-signup" element={<ConfirmSignup />} />
                     <Route path="/f/:token" element={<FormLink />} />
                     <Route path="/verify-email" element={<VerifyEmail />} />
                     <Route path="/reset-password" element={<ResetPassword />} />
                     <Route path="/sentry-test" element={<SentryTest />} />
                    {/* ========== REGULAR USER ROUTES (ProtectedRoute) ========== */}
                    <Route
                      path="/onboarding"
                      element={
                        <ProtectedRoute>
                          <Onboarding />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/conversations-analytics"
                      element={
                        <ProtectedRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyConversationsAnalytics />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/conversations"
                      element={
                        <ProtectedRoute>
                          <PlatformAdminInterceptor>
                            <ConversationsSwitch />
                          </PlatformAdminInterceptor>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/conversations/quick-replies"
                      element={
                        <ProtectedRoute>
                          <QuickRepliesManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/conversations/escalations"
                      element={
                        <ProtectedRoute requiredRole="tenant_admin">
                          <Suspense fallback={<PageLoader />}>
                            <LazyEscalationSettings />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    {/* The old direct route for the new screen. It now redirects instead of
                        mounting V2 on its own: mounting it here left the version toggle reading a
                        preference that did not match the screen actually on display, so it could
                        read "Classic" over the new screen and silently rewrite the preference. */}
                    <Route
                      path="/conversations-v2"
                      element={<Navigate to="/conversations?v=2" replace />}
                    />
                    <Route
                      path="/tenant-admin"
                      element={
                        <ProtectedRoute>
                          <TenantAdmin />
                        </ProtectedRoute>
                      }
                    />
                     <Route
                       path="/tenant-admin/users"
                       element={
                         <ProtectedRoute>
                           <TenantUserManagement />
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/user-management"
                       element={
                         <ProtectedRoute>
                           <TenantUserManagement />
                         </ProtectedRoute>
                       }
                     />
                    <Route
                      path="/tenant-admin/billing"
                      element={
                        <ProtectedRoute>
                          <TenantBilling />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/tenant-admin/payments"
                      element={
                        <ProtectedRoute requiredRole="tenant_admin">
                          <Suspense fallback={<PageLoader />}>
                            <LazyPaymentSettings />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/tenant-admin/credentials"
                      element={
                        <ProtectedRoute requiredRole="tenant_admin">
                          <Suspense fallback={<PageLoader />}>
                            <LazyTenantAdminCredentials />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/tenant-admin/testing"
                      element={
                        <ProtectedRoute>
                          <TenantTesting />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/tenant-admin/conversation-simulator"
                      element={
                        <ProtectedRoute requiredRole="tenant_admin">
                          <Suspense fallback={<PageLoader />}>
                            <LazyTenantConversationSimulator />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/tenant-admin/products-services"
                      element={
                        <ProtectedRoute>
                          <TenantProductsServices />
                        </ProtectedRoute>
                      }
                    />
                     <Route
                       path="/tenant-admin/products-services/tires"
                       element={
                         <ProtectedRoute>
                           <ModuleGuard module="tire_inventory">
                             <TenantTireManagement />
                           </ModuleGuard>
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/tenant-admin/context-stores"
                       element={
                         <ProtectedRoute requiredRole="tenant_admin">
                           <Suspense fallback={<PageLoader />}>
                             <LazyTenantContextStores />
                           </Suspense>
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/tenant-admin/whatsapp-flows"
                       element={
                         <ProtectedRoute requiredRole="tenant_admin">
                           <Suspense fallback={<PageLoader />}>
                             <LazyTenantWhatsAppFlows />
                           </Suspense>
                         </ProtectedRoute>
                       }
                     />
                     {/* A-37: the routes register unconditionally so ModuleGuard can answer
                         "this feature is not enabled for your account" instead of the router
                         answering 404. A 404 is the right answer to an invented address, not to
                         a switched-off feature. The sidebar still hides the entry, so no dead
                         link appears in the menu. */}
                         <Route
                           path="/tenant-admin/app-studio"
                           element={
                             <ProtectedRoute requiredRole="tenant_admin">
                               <ModuleGuard module="app_studio">
                                 <Suspense fallback={<PageLoader />}>
                                   <LazyTenantAppStudio />
                                 </Suspense>
                               </ModuleGuard>
                             </ProtectedRoute>
                           }
                         />
                         <Route
                           path="/tenant-admin/app-studio/:appSlug"
                           element={
                             <ProtectedRoute requiredRole="tenant_admin">
                               <ModuleGuard module="app_studio">
                                 <Suspense fallback={<PageLoader />}>
                                   <LazyTenantAppStudioApp />
                                 </Suspense>
                               </ModuleGuard>
                             </ProtectedRoute>
                           }
                         />
                         <Route
                           path="/tenant-admin/app-studio/:appSlug/pages/:pageSlug"
                           element={
                             <ProtectedRoute requiredRole="tenant_admin">
                               <ModuleGuard module="app_studio">
                                 <Suspense fallback={<PageLoader />}>
                                   <LazyAppStudioBuilder />
                                 </Suspense>
                               </ModuleGuard>
                             </ProtectedRoute>
                           }
                         />
                         <Route
                           path="/tenant-admin/app-studio/:appSlug/view/:pageSlug"
                           element={
                             <ProtectedRoute requiredRole="tenant_admin">
                               <ModuleGuard module="app_studio">
                                 <Suspense fallback={<PageLoader />}>
                                   <LazyTenantAppStudioPageViewer />
                                 </Suspense>
                               </ModuleGuard>
                             </ProtectedRoute>
                           }
                         />
                     <Route
                       path="/tenant-admin/custom-extensions"
                       element={
                         <ProtectedRoute requiredRole="tenant_admin">
                           <Suspense fallback={<PageLoader />}>
                             <LazyTenantCustomExtensions />
                           </Suspense>
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/tenant-admin/products"
                       element={
                         <ProtectedRoute requiredRole="tenant_admin">
                           <TenantAdminProducts />
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/tenant-admin/customers"
                       element={
                         <ProtectedRoute>
                           <TenantAdminCustomers />
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/tenant-admin/orders"
                       element={
                         <ProtectedRoute>
                           <TenantAdminOrders />
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/tenant-admin/document-review"
                       element={
                         <ProtectedRoute>
                           <TenantAdminDocumentReview />
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/tenant-admin/business-hours"
                       element={
                         <ProtectedRoute requiredRole="tenant_admin">
                           <TenantAdminBusinessHours />
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/tenant-admin/rule-sets"
                       element={
                         <ProtectedRoute requiredRole="tenant_admin">
                           <TenantAdminRuleSets />
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/tenant-admin/document-types"
                       element={
                         <ProtectedRoute requiredRole="tenant_admin">
                           <TenantAdminDocumentTypes />
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/tenant-admin/agendamiento"
                       element={
                         <ProtectedRoute requiredRole="tenant_admin">
                           <Suspense fallback={<PageLoader />}>
                             <LazyTenantAdminCalendarBooking />
                           </Suspense>
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/tenant-admin/api-monitoring"
                       element={
                         <ProtectedRoute requiredRole="tenant_admin">
                           <TenantAdminAPIMonitoring />
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/tenant-admin/random-erp-testing"
                       element={
                         <ProtectedRoute requiredRole="tenant_admin">
                           <TenantAdminRandomERPTesting />
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/partners"
                       element={
                         <ProtectedRoute requiredRole="tenant_admin">
                           <TenantAdminPartnerDirectory />
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/tenant-admin/templates"
                       element={
                         <ProtectedRoute requiredRole="tenant_admin">
                           <TenantTemplatesLayout />
                         </ProtectedRoute>
                       }
                     >
                       <Route index element={<Navigate to="email" replace />} />
                       <Route path="email" element={<Suspense fallback={<PageLoader />}><LazyTenantEmailTemplates /></Suspense>} />
                       <Route
                         path="whatsapp"
                         element={<TenantWhatsAppTemplates />}
                       >
                         <Route index element={<Navigate to="templates" replace />} />
                         <Route path="settings" element={<WhatsAppSettingsTab />} />
                         <Route path="templates" element={<WhatsAppTemplatesTab />} />
                         <Route path="analytics" element={<WhatsAppAnalyticsTab />} />
                       </Route>
                     </Route>
                     <Route
                       path="/tenant-admin/templates/email/settings"
                       element={
                         <ProtectedRoute requiredRole="tenant_admin">
                           <EmailProviderSettings />
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/ai-agents"
                       element={
                         <ProtectedRoute requiredRole="tenant_admin">
                           <Suspense fallback={<PageLoader />}>
                             <LazyTenantAIAgents />
                           </Suspense>
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/ai-workflows"
                       element={
                         <ProtectedRoute requiredRole="tenant_admin">
                           <Suspense fallback={<PageLoader />}>
                             <LazyTenantAIWorkflows />
                           </Suspense>
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/ai-settings"
                       element={
                         <ProtectedRoute requiredRole="tenant_admin">
                           <Suspense fallback={<PageLoader />}>
                             <LazyTenantAISettings />
                           </Suspense>
                         </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/ai-workflows/builder"
                        element={
                          <ProtectedRoute requiredRole="tenant_admin">
                            <Suspense fallback={<PageLoader />}>
                              <LazyTenantWorkflowBuilder />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                     <Route
                       path="/whatsapp-admin"
                        element={
                          <ProtectedRoute>
                            <WhatsAppAdminRegular />
                          </ProtectedRoute>
                        }
                      />
                     <Route
                       path="/instagram-admin"
                        element={
                          <ProtectedRoute>
                            <ComingSoon />
                          </ProtectedRoute>
                        }
                      />
                     <Route
                       path="/tiktok-admin"
                        element={
                          <ProtectedRoute>
                            <ComingSoon />
                          </ProtectedRoute>
                        }
                      />
                     <Route
                       path="/facebook-admin"
                        element={
                          <ProtectedRoute>
                            <ComingSoon />
                          </ProtectedRoute>
                        }
                      />
                     <Route
                       path="/phone-admin"
                        element={
                          <ProtectedRoute>
                            <ComingSoon />
                          </ProtectedRoute>
                        }
                      />
                     <Route
                       path="/sms-admin"
                        element={
                          <ProtectedRoute>
                            <ComingSoon />
                          </ProtectedRoute>
                        }
                      />
                     <Route
                       path="/email-admin"
                        element={
                          <ProtectedRoute>
                            <ComingSoon />
                          </ProtectedRoute>
                        }
                      />
                     <Route
                       path="/billing"
                       element={
                         <ProtectedRoute>
                           <TenantBilling />
                         </ProtectedRoute>
                       }
                     />
                     <Route
                       path="/settings"
                       element={
                         <ProtectedRoute>
                           <Suspense fallback={<PageLoader />}>
                             <LazySettings />
                           </Suspense>
                         </ProtectedRoute>
                       }
                     />

                    {/* ========== PLATFORM ADMIN ROUTES (PlatformAdminRoute) ========== */}
                    
                    {/* Global Platform Admin Routes */}
                    <Route
                      path="/platform-admin"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdmin />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/users"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdminUsers />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    {/* Labs → Voice Lab (Demo) — admin-gated; rehomed from the former dev-preview /voice-lab route */}
                    <Route
                      path="/platform-admin/voice-lab"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyVoiceLab />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/forms"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdminFormLinks />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/spaces"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdminTenants />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    {/* PL-01: unified under /platform-admin/spaces — this path stays as a
                        permanent redirect so old bookmarks and links keep working. */}
                    <Route
                      path="/platform-admin/tenants"
                      element={<Navigate to="/platform-admin/spaces" replace />}
                    />
                    <Route
                      path="/platform-admin/spaces/new"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdminSpaceWizard />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/spaces/:tenantId"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdminSpaceWizard />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                     <Route
                       path="/platform-admin/ai-management"
                       element={
                         <PlatformAdminRoute>
                           <Suspense fallback={<PageLoader />}>
                             <LazyPlatformAdminAIManagement />
                           </Suspense>
                         </PlatformAdminRoute>
                       }
                     />
                     <Route
                       path="/platform-admin/test-models"
                       element={
                         <PlatformAdminRoute>
                           <Suspense fallback={<PageLoader />}>
                             <LazyPlatformAdminTestModels />
                           </Suspense>
                         </PlatformAdminRoute>
                       }
                     />
                     <Route
                       path="/platform-admin/ai-templates"
                       element={
                         <PlatformAdminRoute>
                           <Suspense fallback={<PageLoader />}>
                             <LazyPlatformAdminAITemplates />
                           </Suspense>
                         </PlatformAdminRoute>
                       }
                     />
                     <Route
                       path="/platform-admin/templates"
                       element={
                         <PlatformAdminRoute>
                           <Suspense fallback={<PageLoader />}>
                             <LazyPlatformAdminTemplates />
                           </Suspense>
                         </PlatformAdminRoute>
                       }
                     />
                    <Route
                      path="/platform-admin/billing"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdminBilling />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/logging"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdminLogging />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                     <Route
                       path="/platform-admin/system-health"
                       element={
                         <PlatformAdminRoute>
                           <Suspense fallback={<PageLoader />}>
                             <LazySystemHealth />
                           </Suspense>
                         </PlatformAdminRoute>
                       }
                     />
                     <Route
                       path="/platform-admin/rehearsal-environment"
                       element={
                         <PlatformAdminRoute>
                           <Suspense fallback={<PageLoader />}>
                             <LazyPlatformAdminRehearsalEnvironment />
                           </Suspense>
                         </PlatformAdminRoute>
                       }
                     />
                     <Route
                       path="/platform-admin/api-health"
                       element={
                         <PlatformAdminRoute>
                           <Suspense fallback={<PageLoader />}>
                             <LazyPlatformAdminAPIHealth />
                           </Suspense>
                         </PlatformAdminRoute>
                       }
                     />
                     <Route
                       path="/platform-admin/webhook-security"
                       element={
                         <PlatformAdminRoute>
                           <PlatformAdminWebhookSecurity />
                         </PlatformAdminRoute>
                       }
                     />
                     <Route
                       path="/platform-admin/testing"
                       element={
                         <PlatformAdminRoute>
                           <Suspense fallback={<PageLoader />}>
                             <LazyPlatformAdminTesting />
                           </Suspense>
                         </PlatformAdminRoute>
                       }
                     />
                     <Route
                       path="/platform-admin/conversation-testing"
                       element={
                         <PlatformAdminRoute>
                           <Suspense fallback={<PageLoader />}>
                             <LazyPlatformAdminConversationTesting />
                           </Suspense>
                         </PlatformAdminRoute>
                       }
                     />
                     <Route
                       path="/platform-admin/conversation-simulator"
                       element={
                         <PlatformAdminRoute>
                           <Suspense fallback={<PageLoader />}>
                             <LazyPlatformAdminConversationSimulator />
                           </Suspense>
                         </PlatformAdminRoute>
                       }
                     />
                    <Route
                      path="/platform-admin/profile"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazySettings />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/settings"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazySettings />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/whatsapp"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyWhatsAppAdminPlatform />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />

                    {/* Tenant Feature Selection Routes (without tenant ID) */}
                    <Route
                      path="/platform-admin/tenant/ai-agents"
                      element={
                        <PlatformAdminRoute>
                          <TenantFeatureSelector feature="ai-agents" />
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/tenant/whatsapp"
                      element={
                        <PlatformAdminRoute>
                          <TenantFeatureSelector feature="whatsapp" />
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/tenant/analytics"
                      element={
                        <PlatformAdminRoute>
                          <TenantFeatureSelector feature="analytics" />
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/tenant/logging"
                      element={
                        <PlatformAdminRoute>
                          <TenantFeatureSelector feature="logging" />
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/tenant/settings"
                      element={
                        <PlatformAdminRoute>
                          <TenantFeatureSelector feature="settings" />
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/tenant/conversations"
                      element={
                        <PlatformAdminRoute>
                          <TenantSelectionInterceptor 
                            feature="conversations"
                            featureTitle="Conversation Management"
                            featureDescription="Manage and monitor customer conversations for a specific tenant"
                          />
                        </PlatformAdminRoute>
                      }
                    />

                    {/* Tenant-Specific Platform Admin Routes */}
                    <Route
                      path="/platform-admin/tenant/:tenantId/conversations"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdminTenantConversations />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/tenant/:tenantId/ai-agents"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdminTenantAIAgents />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/tenant/:tenantId/ai-agents/workflow-builder"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdminTenantWorkflowBuilder />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/tenant/:tenantId/products-services"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdminTenantProducts />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/tenant/:tenantId/whatsapp"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdminTenantWhatsApp />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/tenant/:tenantId/analytics"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdminTenantAnalytics />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/tenant/:tenantId/logging"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdminTenantLogging />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/tenant/:tenantId/users"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdminTenantUsers />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/tenant/:tenantId/settings"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdminTenantSettings />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/tenant/:tenantId/runtime-config"
                      element={
                        <PlatformAdminRoute>
                          <Suspense fallback={<PageLoader />}>
                            <LazyPlatformAdminTenantRuntimeConfig />
                          </Suspense>
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/tenant/:tenantId/conversation/:conversationId"
                      element={
                        <PlatformAdminRoute>
                          <TenantConversationDetails />
                        </PlatformAdminRoute>
                      }
                    />
                    <Route
                      path="/platform-admin/tenant/:tenantId/conversation/:conversationId/messages"
                      element={
                        <PlatformAdminRoute>
                          <TenantConversationMessages />
                        </PlatformAdminRoute>
                      }
                    />

                    {/* Catch all route */}
                    {/* Payment result pages (public - no auth required) */}
                    <Route path="/payment-success" element={<PaymentSuccess />} />
                    <Route path="/payment-failed" element={<PaymentFailed />} />
                    <Route path="/payment-error" element={<PaymentFailed />} />
                    <Route path="/payment-pending" element={<PaymentPending />} />
                    <Route path="/payment-timeout" element={<PaymentFailed />} />

                    <Route path="*" element={<NotFound />} />
                      </Routes>
                      </Suspense>
                      <GlobalAICopilot />
                      </AICopilotProvider>
                    </AISuggestionsProvider>
                  </UserImpersonationProvider>
              </TenantSelectionProvider>
            </PlatformAdminProvider>
          </AuthProvider>
          </BrowserRouter>
          <Toaster />
          <Sonner />
        </TooltipProvider>
      </Sentry.ErrorBoundary>
    </PersistQueryClientProvider>
  );
}

export default App;